import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Award, Gift, Building2, Palette, Heart, Feather, Sticker, Target, Dices } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_SLIDES = [
  { src: "/hero/01-memorial-hanging.png",     caption: "Hung with love — a walnut memorial finds its place." },
  { src: "/hero/02-father-son-gift.png",      caption: "A gift that'll outlast the holiday season." },
  { src: "/hero/03-poker-friends.png",        caption: "Monogrammed chips, Thursday night legends." },
  { src: "/hero/04-mother-baby-plaque.png",   caption: "The nursery's first heirloom." },
  { src: "/hero/05-couple-anniversary.png",   caption: "Twenty-five years in a maple box." },
  { src: "/hero/06-firefighter-award.png",    caption: "Brass for those who run toward it." },
  { src: "/hero/07-grandfather-grandson.png", caption: "Passed down, pass it forward." },
];

const services = [
  { icon: Heart, title: "Births", desc: "Newborn keepsakes and family heirlooms etched to last generations." },
  { icon: Feather, title: "Memorials", desc: "Tender tributes for the people and pets we can't let go of — walnut, brass and slate." },
  { icon: Award, title: "Awards & Recognition", desc: "Corporate awards, trophies, honor plaques." },
  { icon: Building2, title: "Business Branding", desc: "Signage, logo plates, office identity." },
  { icon: Palette, title: "Artistic Projects", desc: "Photo-realistic engravings and custom art." },
  { icon: Gift, title: "Personalized Gifts", desc: "Weddings, anniversaries, thoughtful moments." },
  { icon: Sticker, title: "Stickers & Color Prints", desc: "Die-cut vinyl stickers, photo magnets, printed keychains — vivid full-color pieces for every surface." },
  { icon: Target, title: "Firearm Engraving", desc: "Monograms, scrollwork and custom text on slides, grips and lowers — heirloom-grade personalization." },
  { icon: Dices, title: "Poker Chips & Game Sets", desc: "Monogrammed clay poker chips, custom dice and card sets — unforgettable groomsman and birthday gifts." },
];

const featured = [
  { title: "Custom Designs For Every Occasion", material: "Multi-Material", image: "https://images.unsplash.com/photo-1513116476489-7635e79feb27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" },
  { title: "Foundry Medal", material: "Brushed Brass", image: "https://images.unsplash.com/photo-1777738275365-46c9d2e350b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { title: "Studio Logo Sign", material: "Cold-rolled Steel", image: "https://images.unsplash.com/photo-1616441065120-81e9e146d3ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
  { title: "Anniversary Box", material: "Sugar Maple", image: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" },
];

// Motion presets hoisted so props keep stable references across renders
const SLIDE_MOTION = { initial: { opacity: 0, scale: 1.05 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0 }, transition: { opacity: { duration: 1.4 }, scale: { duration: 7, ease: "linear" } } };
const CAPTION_MOTION = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -6 }, transition: { duration: 0.4 } };
const REVEAL_MOTION = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function Home() {
  const nav_go = useNavigate();
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero" className="relative overflow-hidden">
        <div data-testid="hero-slideshow" className="absolute inset-0 -z-10">
          <AnimatePresence mode="sync">
            <motion.img
              key={slide}
              data-testid={`hero-slide-${slide}`}
              src={HERO_SLIDES[slide].src}
              alt={HERO_SLIDES[slide].caption}
              {...SLIDE_MOTION}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0" style={{background: "linear-gradient(180deg, rgba(20,15,10,0.45) 0%, rgba(20,15,10,0.78) 100%)"}}/>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-40 text-white">
          <div className="uppercase text-xs tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Est. Craftsmanship • Since 2020</div>
          <motion.h1 initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:0.8}}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-3xl leading-[1.05]">
            Engraved with intention.<br/>
            <span style={{color: "var(--accent-metal)"}}>Made to be kept.</span>
          </motion.h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-90">
            EZ JEM Designs transforms wood, metal, stone and glass into meaningful heirlooms —
            for births, memorials, awards, brands and gifts that carry the weight of a moment.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/design-studio"><Button data-testid="hero-cta-design" className="btn-metal rounded-full px-7 py-6 text-base font-semibold">Enter Design Studio <ArrowRight className="ml-2" size={18}/></Button></Link>
            <Link to="/gallery"><Button data-testid="hero-cta-gallery" variant="outline" className="rounded-full px-7 py-6 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">Explore Idea Library</Button></Link>
          </div>
          {/* Slide caption + dots */}
          <div className="mt-14 flex items-center gap-5">
            <div className="flex gap-2" data-testid="hero-dots">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={HERO_SLIDES[i].src}
                  data-testid={`hero-dot-${i}`}
                  onClick={() => setSlide(i)}
                  aria-label={`Show scene ${i+1}`}
                  className={`h-1.5 rounded-full transition-all ${i===slide ? "w-10" : "w-5 opacity-50 hover:opacity-80"}`}
                  style={{backgroundColor: i===slide ? "var(--accent-metal)" : "rgba(255,255,255,0.6)"}}
                />
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={slide}
                {...CAPTION_MOTION}
                className="text-sm italic opacity-90 font-serif"
                data-testid="hero-caption"
              >
                {HERO_SLIDES[slide].caption}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="metal-line absolute bottom-0 inset-x-0"/>
      </section>

      {/* SERVICES */}
      <section data-testid="services" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-10 items-end mb-14">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>What we engrave</div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-3 max-w-xl leading-tight" style={{color: "var(--primary)"}}>Six disciplines. One steady hand.</h2>
          </div>
          <p className="text-[color:var(--text-muted)] text-base leading-relaxed max-w-md">Every project passes through the same six-step process — from consultation to hand-finish — regardless of scale.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div key={s.title} {...REVEAL_MOTION} transition={{delay: i*0.05}}
              data-testid={`service-${s.title.toLowerCase().split(/\s+/)[0]}`}
              role="button"
              tabIndex={0}
              onClick={() => nav_go("/design-studio")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav_go("/design-studio"); } }}
              className="card-lift group relative overflow-hidden bg-white p-8 rounded-lg border hairline cursor-pointer focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-metal)]">
              <s.icon size={28} strokeWidth={1.5} style={{color: "var(--secondary)"}}/>
              <h3 className="mt-5 font-serif text-2xl" style={{color: "var(--primary)"}}>{s.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">{s.desc}</p>
              <div className="mt-6 metal-line"/>
              <div className="mt-4 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity" style={{color: "var(--secondary)"}}>Start designing <ArrowRight size={12}/></div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="max-w-7xl mx-auto px-6 lg:px-10 my-24">
        <div className="rounded-lg p-12 sm:p-16 relative overflow-hidden" style={{background: "var(--primary)"}}>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="uppercase text-xs tracking-[0.3em]" style={{color: "var(--accent-metal)"}}>Ready when you are</div>
              <h3 className="font-serif text-4xl sm:text-5xl mt-3 text-white leading-tight">Bring us your idea. We'll draw the rest.</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link to="/custom-order"><Button data-testid="cta-custom-order" className="btn-metal rounded-full px-7 py-6 text-base font-semibold">Start a custom order</Button></Link>
              <Link to="/bulk"><Button data-testid="cta-bulk" variant="outline" className="rounded-full px-7 py-6 text-base bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">Bulk & business</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
